import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {AiOutlineLoading3Quarters} from "react-icons/ai";
import {FaArrowLeft, FaCompass} from "react-icons/fa";
import {RECOMMENDATION_CONTEXTS} from "../config/recommendationContexts";
import {useDebugMode} from "../hooks/useDebugMode";
import RecommendationWhyBadge from "./RecommendationWhyBadge";

const cacheArticleForNavigation = (story) => {
  if (!story?.uuid) return;
  try {
    sessionStorage.setItem(`neuron:article:${story.uuid}`, JSON.stringify(story));
  } catch {
    /* ignore quota errors */
  }
};

const InfoTooltip = () => {
  const debugMode = useDebugMode();
  return (
    <div className="relative group">
      <button
        type="button"
        className="p-2 rounded-full bg-black/40 text-white/80 hover:text-white"
        aria-label="What is this?"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M12 20a8 8 0 100-16 8 8 0 000 16z"
          />
        </svg>
      </button>
      <div className="absolute right-0 bottom-full mb-3 w-72 max-w-xs text-sm text-black dark:text-white bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md px-3 py-2 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-30 pointer-events-none group-hover:pointer-events-auto">
        <div className="absolute -bottom-1.5 right-4 w-3 h-3 bg-white dark:bg-gray-800 border-r border-b border-gray-200 dark:border-gray-700 rotate-45" />
        {debugMode ? (
          <>
            Same NSL context as the homepage "For You" rail -{" "}
            <code>Homepage For You</code> (id 238) - but rendered as a
            full-viewport snap feed. Clicked items are filtered client-side
            via <code>localStorage.clickedArticles</code> so you don't see
            them again.
          </>
        ) : (
          <>
            Your "For You" feed, served full-screen, one story per swipe.
            Once you tap a card it won't reappear here, so you can keep
            swiping fresh stories until you've worked through your
            personalised queue.
          </>
        )}
      </div>
    </div>
  );
};

const SwipeUpHint = ({visible}) => (
  <div
    className={`pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-8 sm:bottom-10 z-30 flex flex-col items-center transition-all duration-500 ease-out ${
      visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
    }`}
    aria-hidden="true"
  >
    <div className="flex items-center gap-2 rounded-full bg-black/55 backdrop-blur-md px-3.5 py-1.5 shadow-lg ring-1 ring-white/10">
      <span className="swipe-hint-arrow inline-flex">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-3.5 w-3.5 text-white"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5 15l7-7 7 7"
          />
        </svg>
      </span>
      <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-white/90">
        Swipe up
      </span>
    </div>
    <style>{`
      @keyframes swipeHintRise {
        0%   { transform: translateY(4px); opacity: 0.55; }
        50%  { transform: translateY(-4px); opacity: 1; }
        100% { transform: translateY(4px); opacity: 0.55; }
      }
      .swipe-hint-arrow {
        animation: swipeHintRise 1.6s cubic-bezier(0.45, 0, 0.55, 1) infinite;
      }
      @media (prefers-reduced-motion: reduce) {
        .swipe-hint-arrow { animation: none; }
      }
    `}</style>
  </div>
);

/** Suppressed entirely when the API gives us no base_score to report. */
const ScorePill = ({value}) =>
  value == null ? null : (
    <span className="inline-flex items-center gap-1.5 rounded-sm bg-paper/92 px-2 py-1 font-sans text-kicker font-semibold tabular-nums text-pencil">
      {value}% match
    </span>
  );

const VerticalCarousel = ({
  userEmail,
  personalisedData = {},
  loadingStates = {},
  fetchRecommendationsForContext,
}) => {
  const navigate = useNavigate();
  const [clickedArticles, setClickedArticles] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("clickedArticles")) || [];
    } catch {
      return [];
    }
  });

  const [hintDismissed, setHintDismissed] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const scrollRef = useRef(null);

  useLayoutEffect(() => {
    const measure = () => {
      const headerEl = document.querySelector("header");
      setHeaderHeight(headerEl?.getBoundingClientRect().height ?? 0);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const isLoading = loadingStates[RECOMMENDATION_CONTEXTS.homepageForYou];
  const data = useMemo(
    () => personalisedData[RECOMMENDATION_CONTEXTS.homepageForYou] || [],
    [personalisedData]
  );

  useEffect(() => {
    if (!userEmail) return;
    fetchRecommendationsForContext(RECOMMENDATION_CONTEXTS.homepageForYou);
  }, [fetchRecommendationsForContext, userEmail]);

  const stories = useMemo(() => {
    if (!data.length) return [];
    return data
      .map(({id, score,
          baseScore, name, description, metadata, recommendationSource, recommendationExplanation}) => {
        if (!metadata) return null;
        return {
          uuid: id,
          originalTitle: metadata.originalTitle || name || "Untitled",
          aiSummary: metadata.summary || description || "",
          image: metadata.image || "",
          source: metadata.source || "",
          aiCategorization: metadata.aiCategorization || "",
          timeToRead: metadata.timeToRead || "",
          createdDate: metadata.createdDate || "",
          // base_score is the similarity to the reader's profile. The final
          // `score` is post-damping (impression fatigue can cut it ~15x), which
          // is why the top card used to read "2% match".
          matchPercentage:
            typeof baseScore === "number" ? Math.round(baseScore * 100) : null,
          recommendationSource: recommendationSource || "",
          recommendationExplanation: recommendationExplanation || null,
        };
      })
      .filter(Boolean)
      .filter((story) => !clickedArticles.includes(story.uuid));
  }, [data, clickedArticles]);

  useEffect(() => {
    if (hintDismissed) return;
    if (stories.length < 2) return;
    const node = scrollRef.current;
    if (!node) return;
    const dismiss = () => setHintDismissed(true);
    const events = ["scroll", "touchstart", "wheel", "pointerdown"];
    events.forEach((evt) =>
      node.addEventListener(evt, dismiss, {passive: true, once: true})
    );
    window.addEventListener("keydown", dismiss, {once: true});
    return () => {
      events.forEach((evt) => node.removeEventListener(evt, dismiss));
      window.removeEventListener("keydown", dismiss);
    };
  }, [hintDismissed, stories.length]);

  const handleStoryClick = (story) => {
    if (!story?.uuid) return;
    cacheArticleForNavigation(story);
    const next = clickedArticles.includes(story.uuid)
      ? clickedArticles
      : [...clickedArticles, story.uuid];
    if (next !== clickedArticles) {
      setClickedArticles(next);
      try {
        localStorage.setItem("clickedArticles", JSON.stringify(next));
      } catch {
        /* ignore */
      }
    }
    navigate(`/article/${story.uuid}`);
  };

  return (
    <div
      className="vertical-carousel relative"
      style={{height: `calc(100dvh - ${headerHeight}px)`}}
    >
      <div className="hidden sm:block absolute bottom-6 right-6 z-20">
        <InfoTooltip />
      </div>

      {stories.length > 1 && (
        <SwipeUpHint visible={!hintDismissed} />
      )}

      {isLoading ? (
        <div className="flex flex-col items-center justify-center h-full">
          <AiOutlineLoading3Quarters className="animate-spin text-5xl text-gray-400 mb-4" />
          <p className="text-sm text-gray-500">Loading your feed…</p>
        </div>
      ) : stories.length > 0 ? (
        <div
          ref={scrollRef}
          className="h-full overflow-y-auto snap-y snap-mandatory scrollbar-hide"
        >
          {stories.map((story) => (
            <article
              key={story.uuid}
              className="snap-start h-full w-full flex items-center justify-center sm:px-4 sm:py-2"
            >
              <div
                onClick={() => handleStoryClick(story)}
                className="relative w-full h-full sm:max-w-xl lg:max-w-2xl sm:max-h-[640px] sm:rounded-2xl overflow-hidden sm:shadow-2xl bg-gray-900 group cursor-pointer"
              >
                {story.image ? (
                  <img
                    src={story.image}
                    alt={story.originalTitle}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-gray-700 to-gray-900" />
                )}

                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/40" />

                <div className="absolute top-4 left-4 right-4 flex items-start justify-between gap-2">
                  <ScorePill value={story.matchPercentage} />
                  <div className="flex items-center gap-2 max-w-[60%]">
                    {story.source && (
                      <span className="rounded-full bg-black/55 backdrop-blur-sm px-2.5 py-1 text-xs font-medium text-white truncate">
                        {story.source}
                      </span>
                    )}
                    <RecommendationWhyBadge
                      explanation={story.recommendationExplanation}
                      source={story.recommendationSource}
                      align="right"
                    />
                  </div>
                </div>

                <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6 text-white">
                  {story.aiCategorization && (
                    <p className="text-[11px] uppercase tracking-wider text-white/70 mb-1.5">
                      {story.aiCategorization}
                      {story.timeToRead ? ` · ${story.timeToRead}` : ""}
                    </p>
                  )}
                  <h2 className="text-2xl sm:text-3xl font-semibold leading-tight mb-2">
                    {story.originalTitle}
                  </h2>
                  {story.aiSummary && (
                    <p className="text-sm text-white/85 line-clamp-3">
                      {story.aiSummary}
                    </p>
                  )}
                  <div className="mt-4 flex items-center">
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-white/90">
                      Read story
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </span>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center text-center h-full px-6">
          <div className="rounded-full bg-blue-50 dark:bg-blue-900/30 p-5 mb-5">
            <FaCompass className="text-4xl text-blue-600 dark:text-blue-300" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1.5">
            You're all caught up
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm leading-relaxed">
            You've worked through every personalised story in your queue.
            Read a few more articles on the homepage and your{" "}
            <em>For You</em> feed will refill in a few minutes.
          </p>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="mt-6 inline-flex items-center gap-2 rounded-lg bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-medium px-4 py-2.5 hover:opacity-90 transition"
          >
            <FaArrowLeft />
            Back to homepage
          </button>
        </div>
      )}
    </div>
  );
};

export default VerticalCarousel;
